import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { API_URL } from './constants';
import axios from 'axios';

const ProductMultiCountryView = () => {
  const [selectedCountries, setSelectedCountries] = useState(['romania', 'italy', 'uk']);
  const [priceIndexData, setPriceIndexData] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [realData, setRealData] = useState([]);
  const [baseProduct, setBaseProduct] = useState('romania_ploom'); // Default base product
  const [relativeData, setRelativeData] = useState([]);
  const [aiSummary, setAiSummary] = useState('');

  // Available countries for chart
  const availableCountries = [
    { id: 'romania', name: 'Romania', code: 'RO' },
    { id: 'italy', name: 'Italy', code: 'IT' },
    { id: 'uk', name: 'United Kingdom', code: 'UK' }
  ];

  // Available products for base selection
  const availableProducts = [
    { id: 'romania_ploom', name: 'Ploom Aura KIT', country: 'Romania', countryCode: 'RO', type: 'ploom' },
    { id: 'italy_ploom', name: 'Ploom X Black', country: 'Italy', countryCode: 'IT', type: 'ploom' },
    { id: 'uk_ploom', name: 'Ploom X Advanced', country: 'UK', countryCode: 'UK', type: 'ploom' },
    { id: 'italy_iluma', name: 'IQOS ILUMA i', country: 'Italy', countryCode: 'IT', type: 'iluma' },
    { id: 'romania_kit', name: 'Kit IQOS ILUMA i ONE', country: 'Romania', countryCode: 'RO', type: 'kit' },
    { id: 'uk_kit', name: 'Kit', country: 'UK', countryCode: 'UK', type: 'kit' }
  ];

  // Fetch real data from API
  const fetchRealData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`${API_URL}/jti/global-merged-data`);
      const { columns: apiColumns, data: apiData } = response.data;
      
      // Convert raw data to objects with column names
      const processedData = apiData.map(row => {
        const obj = {};
        apiColumns.forEach((col, index) => {
          obj[col] = row[index];
        });
        return obj;
      });
      
      setRealData(processedData);
      
      // Process real data for chart
      processRealData(processedData);
      
    } catch (err) {
      console.error('Error fetching real data:', err);
      setError('Failed to load real data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Currency conversion rates (approximate rates, in production you'd want to use a real API)
  const currencyRates = {
    'RON': 0.20, // 1 RON = 0.20 EUR (approximate)
    'GBP': 1.17, // 1 GBP = 1.17 EUR (approximate)
    'EUR': 1.00  // 1 EUR = 1.00 EUR
  };

  // Convert price to EUR based on currency
  const convertToEUR = (price, currency) => {
    const rate = currencyRates[currency] || 1.00;
    return price * rate;
  };

  // Process real data for chart display
  const processRealData = (data) => {
    // Filter for Ploom products and ILUMA i ONE Starter Kit in the three countries
    const productData = data.filter(item => {
      const title = item.title?.toLowerCase() || '';
      const country = item.country?.toLowerCase() || '';
      const retailer = item.retailer?.toLowerCase() || '';
      
      // Check if it's a Ploom product, IQOS ILUMA i/i PRIME, or Kit products in our target countries
      const isPloomProduct = retailer === 'ploom' || title.includes('ploom');
      const isIlumaProduct = (title.includes('iluma i') || title.includes('iluma i prime')) && 
                            (country === 'it' || country === 'italy');
      const isRomaniaKit = title.includes('kit iqos iluma i one') && 
                          (country === 'ro' || country === 'romania');
      const isUKKit = title.includes('kit') && 
                      (country === 'uk' || country === 'united kingdom');
      const isTargetCountry = ['ro', 'it', 'uk'].includes(country) || 
                              ['romania', 'italy', 'uk'].includes(country);
      
      return (isPloomProduct || isIlumaProduct || isRomaniaKit || isUKKit) && isTargetCountry;
    });

    // Group by date and country
    const groupedData = {};
    
    // Country mapping from dataset codes to our chart keys
    const countryMapping = {
      'ro': 'romania',
      'romania': 'romania',
      'it': 'italy', 
      'italy': 'italy',
      'uk': 'uk',
      'united kingdom': 'uk'
    };
    
    productData.forEach(item => {
      const date = item.updated_at || item.date || item.created_at || item.timestamp;
      if (!date) return;
      
      const dateKey = new Date(date).toISOString().split('T')[0];
      if (!groupedData[dateKey]) {
        groupedData[dateKey] = {};
      }
      
      const country = countryMapping[item.country?.toLowerCase()] || item.country?.toLowerCase();
      const rawPrice = parseFloat(item.actual_price || item.regular_price || item.price) || 0;
      const currency = item.currency || 'EUR';
      const title = item.title?.toLowerCase() || '';
      
      // Convert price to EUR
      const priceInEUR = convertToEUR(rawPrice, currency);
      
      // Determine product type and create appropriate key
      let productType = 'ploom'; // default
      if ((title.includes('iluma i') || title.includes('iluma i prime')) && 
          (country === 'italy' || country === 'it')) {
        productType = 'iluma';
      } else if (title.includes('kit iqos iluma i one') && 
                 (country === 'romania' || country === 'ro')) {
        productType = 'kit';
      } else if (title.includes('kit') && 
                 (country === 'uk' || country === 'united kingdom')) {
        productType = 'kit';
      }
      
      const key = `${country}_${productType}`;
      
      if (!groupedData[dateKey][key]) {
        groupedData[dateKey][key] = [];
      }
      
      groupedData[dateKey][key].push(priceInEUR);
    });

    // Calculate average prices and create chart data
    const chartData = Object.keys(groupedData)
      .sort()
      .map(date => {
        const dataPoint = { date };
        
        selectedCountries.forEach(country => {
          // Ploom products
          const ploomPrices = groupedData[date][`${country}_ploom`] || [];
          const avgPloomPrice = ploomPrices.length > 0 ? ploomPrices.reduce((a, b) => a + b, 0) / ploomPrices.length : 0;
          dataPoint[`${country}_ploom`] = Math.round(avgPloomPrice * 100) / 100;
          
          // IQOS ILUMA i/i PRIME (Italy only)
          if (country === 'italy') {
            const ilumaPrices = groupedData[date][`${country}_iluma`] || [];
            const avgIlumaPrice = ilumaPrices.length > 0 ? ilumaPrices.reduce((a, b) => a + b, 0) / ilumaPrices.length : 0;
            dataPoint[`${country}_iluma`] = Math.round(avgIlumaPrice * 100) / 100;
          }
          
          // Kit products (Romania and UK)
          if (country === 'romania' || country === 'uk') {
            const kitPrices = groupedData[date][`${country}_kit`] || [];
            const avgKitPrice = kitPrices.length > 0 ? kitPrices.reduce((a, b) => a + b, 0) / kitPrices.length : 0;
            dataPoint[`${country}_kit`] = Math.round(avgKitPrice * 100) / 100;
          }
        });
        
        return dataPoint;
      });

    console.log('Real data processing results:');
    console.log('Total product records found:', productData.length);
    console.log('Sample product titles:', productData.slice(0, 5).map(item => item.title));
    console.log('Countries found:', [...new Set(productData.map(item => item.country))]);
    console.log('Currencies found:', [...new Set(productData.map(item => item.currency))]);
    console.log('Chart data points:', chartData.length);
    console.log('Sample chart data:', chartData.slice(0, 3));
    console.log('Currency conversion rates used:', currencyRates);

    setPriceIndexData(chartData);
    
    // Calculate relative prices based on selected base product
    calculateRelativePrices(chartData);
  };

  // Calculate relative prices (base = 100)
  const calculateRelativePrices = (chartData) => {
    if (!chartData || chartData.length === 0) return;
    
    const relativeChartData = chartData
      .map(dataPoint => {
        const relativePoint = { date: dataPoint.date };
        
        // Get the base price for the selected product
        const basePrice = dataPoint[baseProduct] || 0;
        
        if (basePrice === 0) {
          // If no base price available for this date, exclude the date entirely
          return null;
        }
        
        // Calculate relative prices (base = 100)
        availableProducts.forEach(product => {
          const productPrice = dataPoint[product.id] || 0;
          const relativePrice = productPrice === 0 ? 0 : Math.round((productPrice / basePrice) * 100 * 100) / 100;
          relativePoint[product.id] = relativePrice;
        });
        
        return relativePoint;
      })
      .filter(Boolean);
    
    setRelativeData(relativeChartData);
    
    // Generate AI summary
    generateAiSummary(relativeChartData);
  };

  // Generate AI summary (mock data for now, can be connected to API later)
  const generateAiSummary = (chartData) => {
    // Mock AI summary - in production, this would call an AI API
    const mockSummary = [
      "Between July and October 2025, the price indices of heated tobacco devices across Italy, Romania, and the United Kingdom showed distinct regional and product-specific trends.",
      "Italy's IQOS ILUMA i maintained the highest and most volatile price levels, frequently spiking above 240% with intermittent drops likely due to data gaps or temporary unavailability.",
      "In contrast, Romania exhibited notable price stability, particularly for the IQOS ILUMA i ONE, which remained close to its baseline (100%) throughout the period.",
      "UK devices, including Ploom X Advanced, were consistently priced below 100%, indicating a more affordable and stable market positioning.",
      "Overall, Italy showed premium pricing with volatility, Romania reflected steady pricing, and the UK demonstrated lower, more consistent price behavior — highlighting differentiated market strategies and consumer pricing environments across regions."
    ];
    
    setAiSummary(mockSummary);
    
    // TODO: Replace with actual AI API call
    // Example API call structure:
    // try {
    //   const response = await axios.post(`${API_URL}/ai/analyze-price-trends`, {
    //     chartData: chartData,
    //     baseProduct: baseProduct,
    //     selectedCountries: selectedCountries,
    //     productDefinitions: availableProducts
    //   });
    //   setAiSummary(response.data.summary);
    // } catch (error) {
    //   console.error('Error generating AI summary:', error);
    //   setAiSummary('Unable to generate AI summary at this time.');
    // }
  };


  // Fetch real data on component mount
  useEffect(() => {
    fetchRealData();
  }, [selectedCountries]);

  // Recalculate relative prices when base product changes
  useEffect(() => {
    if (priceIndexData.length > 0) {
      calculateRelativePrices(priceIndexData);
    }
  }, [baseProduct, priceIndexData]);


  const handleAnalyze = () => {
    // Fetch real data
    fetchRealData();
    setError(null);
  };

  const getLineColors = () => {
    // Unified color scheme: Ploom (blue tones) and ILUMA (green tones)
    const ploomColors = ['#1976d2', '#42a5f5', '#90caf9']; // Blue tones for Ploom
    const ilumaColors = ['#388e3c', '#66bb6a', '#a5d6a7']; // Green tones for ILUMA
    return { ploomColors, ilumaColors };
  };

  const renderChart = () => {
    if (selectedCountries.length === 0 || relativeData.length === 0) return null;

    const { ploomColors, ilumaColors } = getLineColors();
    const baseProductInfo = availableProducts.find(p => p.id === baseProduct);
    
    return (
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={relativeData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="date" 
            tickFormatter={(value) => new Date(value).toLocaleDateString()}
          />
          <YAxis 
            label={{ value: 'Price Index (%)', angle: -90, position: 'insideLeft' }}
            domain={[0, 'dataMax + 20']}
            tickFormatter={(value) => `${value}%`}
          />
          <Tooltip 
            content={({ active, payload, label }) => {
              if (active && payload && payload.length) {
                return (
                  <div style={{
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    padding: '10px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                      Date: {new Date(label).toLocaleDateString()}
                    </div>
                    {payload.map((entry, index) => {
                      const product = availableProducts.find(p => p.id === entry.dataKey);
                      if (!product) return null;
                      
                      // Find the actual price from the original data
                      const actualPrice = priceIndexData.find(d => d.date === label)?.[product.id] || 0;
                      
                      return (
                        <div key={index} style={{ marginBottom: '3px', fontSize: '0.9em' }}>
                          <span style={{ color: entry.color, fontWeight: 'bold' }}>
                            {product.countryCode} - {product.name}:
                          </span>
                          <span style={{ marginLeft: '5px' }}>
                            {entry.value}% (€{actualPrice})
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              }
              return null;
            }}
          />
          <Legend />
          
          {/* Render lines for all products */}
          {availableProducts.map((product, index) => {
            let color;
            let strokeWidth = 2;
            
            // Determine color based on product type
            if (product.type === 'ploom') {
              color = ploomColors[index % ploomColors.length];
            } else if (product.type === 'iluma') {
              color = ilumaColors[0];
            } else if (product.type === 'kit') {
              color = ilumaColors[1];
            }
            
            // Highlight base product
            if (product.id === baseProduct) {
              strokeWidth = 4;
            }
            
            return (
              <Line
                key={product.id}
                type="monotone"
                dataKey={product.id}
                stroke={color}
                strokeWidth={strokeWidth}
                name={`${product.countryCode} - ${product.name}`}
                dot={{ r: product.id === baseProduct ? 6 : 4 }}
                strokeDasharray={product.id === baseProduct ? '0' : '0'}
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Price Index View - Relative Multi-Country Comparison
      </Typography>
      
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
        Relative price index (base = 100) vs competitor "flagship" per market
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Controls */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Analysis Controls
            </Typography>

            {/* Country Selection */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Countries</InputLabel>
              <Select
                multiple
                value={selectedCountries}
                onChange={(e) => setSelectedCountries(e.target.value)}
                renderValue={(selected) => (
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {selected.map((value) => {
                      const country = availableCountries.find(c => c.id === value);
                      return (
                        <Chip 
                          key={value} 
                          label={country?.code || value} 
                          size="small" 
                          color="primary" 
                        />
                      );
                    })}
                  </Stack>
                )}
              >
                {availableCountries.map((country) => (
                  <MenuItem key={country.id} value={country.id}>
                    {country.code} - {country.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Base Product Selection */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Base Product (100%)</InputLabel>
              <Select
                value={baseProduct}
                onChange={(e) => setBaseProduct(e.target.value)}
                label="Base Product (100%)"
              >
                {availableProducts.map((product) => (
                  <MenuItem key={product.id} value={product.id}>
                    {product.countryCode} - {product.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              onClick={handleAnalyze}
              disabled={loading}
              fullWidth
            >
              {loading ? 'Loading...' : 'Analyze Price Index'}
            </Button>
          </Paper>

          {/* AI Summary Card */}
          {aiSummary && (
            <Paper sx={{ p: 2, mt: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                AI Analysis Summary
              </Typography>
              {Array.isArray(aiSummary) ? (
                aiSummary.map((paragraph, index) => (
                  <Typography 
                    key={index}
                    variant="body2" 
                    sx={{ 
                      lineHeight: 1.5,
                      textAlign: 'justify',
                      color: 'text.primary',
                      fontSize: '0.85rem',
                      mb: index < aiSummary.length - 1 ? 1.5 : 0
                    }}
                  >
                    {paragraph}
                  </Typography>
                ))
              ) : (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    lineHeight: 1.5,
                    textAlign: 'justify',
                    color: 'text.primary',
                    fontSize: '0.85rem'
                  }}
                >
                  {aiSummary}
                </Typography>
              )}
            </Paper>
          )}
        </Grid>

        {/* Chart */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Relative Price Index Over Time
              </Typography>
              
              {/* Base Product Information */}
              {baseProduct && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                  <Typography variant="subtitle1" color="primary.contrastText">
                    Base Product (100%): {availableProducts.find(p => p.id === baseProduct)?.countryCode} - {availableProducts.find(p => p.id === baseProduct)?.name}
                  </Typography>
                </Box>
              )}
              
              {/* Product Information in Chart Header */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Grid container spacing={2}>
                  {/* IQOS Products - Left Side */}
                  <Grid item xs={6}>
                    <Typography variant="h6" sx={{ mb: 2, textAlign: 'center', color: '#388e3c' }}>
                      IQOS Products
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Box sx={{ textAlign: 'center', p: 1, border: '2px solid #388e3c', borderRadius: 1, bgcolor: '#e8f5e8' }}>
                          <Typography variant="subtitle2" color="primary">
                            🇮🇹 Italy
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            IQOS ILUMA i
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ textAlign: 'center', p: 1, border: '2px solid #66bb6a', borderRadius: 1, bgcolor: '#f1f8e9' }}>
                          <Typography variant="subtitle2" color="primary">
                            🇷🇴 Romania
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            Kit IQOS ILUMA i ONE
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ textAlign: 'center', p: 1, border: '2px solid #a5d6a7', borderRadius: 1, bgcolor: '#f9fbe7' }}>
                          <Typography variant="subtitle2" color="primary">
                            🇬🇧 UK
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            Kit
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                  
                  {/* Ploom Products - Right Side */}
                  <Grid item xs={6}>
                    <Typography variant="h6" sx={{ mb: 2, textAlign: 'center', color: '#1976d2' }}>
                      Ploom Products
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Box sx={{ textAlign: 'center', p: 1, border: '2px solid #1976d2', borderRadius: 1, bgcolor: '#e3f2fd' }}>
                          <Typography variant="subtitle2" color="primary">
                            🇷🇴 Romania
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            Ploom Aura KIT
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ textAlign: 'center', p: 1, border: '2px solid #42a5f5', borderRadius: 1, bgcolor: '#e1f5fe' }}>
                          <Typography variant="subtitle2" color="primary">
                            🇮🇹 Italy
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            Ploom X Black
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ textAlign: 'center', p: 1, border: '2px solid #90caf9', borderRadius: 1, bgcolor: '#f3e5f5' }}>
                          <Typography variant="subtitle2" color="primary">
                            🇬🇧 UK
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            Ploom X Advanced
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
              
              {loading ? (
                <Box sx={{ 
                  height: 400, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  flexDirection: 'column'
                }}>
                  <CircularProgress />
                  <Typography variant="h6" sx={{ mt: 2 }}>
                    Loading real data...
                  </Typography>
                </Box>
              ) : selectedCountries.length > 0 && relativeData.length > 0 ? (
                renderChart()
              ) : (
                        <Box sx={{ 
                          height: 400, 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          color: 'text.secondary'
                        }}>
                          <Typography variant="h6">
                            {selectedCountries.length === 0 
                              ? 'Select countries to view price comparison' 
                              : 'No real data available for selected countries'
                            }
                          </Typography>
                        </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Key Insights */}
      {selectedCountries.length > 0 && (
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Key Insights
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Blue tones: Ploom devices (Aura KIT, X Black, X Advanced)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Green tones: IQOS ILUMA i (Italy), Kit IQOS ILUMA i ONE (Romania), Kit (UK)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Thick line: Selected base product (100% reference)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Y-axis shows relative price index as percentage
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Select different base products to compare relative pricing
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Analysis Summary
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Selected Countries: {selectedCountries.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Base Product: {availableProducts.find(p => p.id === baseProduct)?.countryCode} - {availableProducts.find(p => p.id === baseProduct)?.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Data Points: {relativeData.length} records
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Data Source: Real Data
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View Type: Relative Price Index (Base = 100%)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default ProductMultiCountryView;
